import { useState } from 'react';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Group, Loader, SimpleGrid, Skeleton, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconLock } from '@tabler/icons-react';
import { BILLING_CONSTRAINTS } from '@/lib/constants/account';
import type { ICardSummary } from '@/lib/types/Billing';
import { cardBillingDetailsSchema, type CardBillingFormValues } from '../../schemas/billingSchemas';
import { CardEntryUtils } from '../../CardEntryUtils';
import { useCardConfirm, useCardSetup } from '../../hooks/useCardEntry';
import { useStripePaymentElement } from '../../hooks/useStripePaymentElement';
import { ControlledSelect, ControlledTextInput, COUNTRY_OPTIONS } from './BillingFormControls';

const DEFAULT_SUBMIT_LABEL = 'Save Payment Method';
const CARD_ERROR_TITLE = 'Card not saved';

export interface CardFormError {
  title: string;
  message: string;
}

interface StripeCardFormProps {
  defaults: CardBillingFormValues;
  /**
   * Runs after the card is saved on the platform. A rejection here is the caller's
   * follow-up failing (e.g. the upgrade), not a card problem — the caller owns that
   * state and normally unmounts this form as soon as it is invoked.
   */
  onSaved: (card: ICardSummary) => void | Promise<void>;
  /** Omit to hide Cancel (the Account Closed screen has nowhere to go back to). */
  onCancel?: () => void;
  submitLabel?: string;
  /** An error the caller wants shown on mount (e.g. a charge that failed after the card was saved). */
  initialError?: CardFormError | null;
}

/**
 * Embedded Stripe Elements card entry (ADR-010; notes/billing-card-entry.md D2–D5;
 * contract ref_card_entry_contract.md §2.1–2.2). The dashboard owns name + billing
 * address; card number, expiry and CVC live in Stripe's iframe fields. Submit:
 * validate ours → stripe.confirmSetup → platform confirm → onSaved. Every failure
 * keeps the form usable with the reason inline.
 */
export function StripeCardForm({ defaults, onSaved, onCancel, submitLabel, initialError }: StripeCardFormProps) {
  const [setupAttempt, setSetupAttempt] = useState(0);
  const setup = useCardSetup(setupAttempt);
  const element = useStripePaymentElement({
    publishableKey: setup.data?.publishableKey,
    clientSecret: setup.data?.clientSecret,
  });
  const confirmMutation = useCardConfirm();
  const [formError, setFormError] = useState<CardFormError | null>(initialError ?? null);
  const cardError = (message: string) => setFormError({ title: CARD_ERROR_TITLE, message });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Stripe verified the card but the platform save failed: the retry saves the same
  // verified intent without asking Stripe (or the customer) again.
  const [verifiedSetupIntentId, setVerifiedSetupIntentId] = useState<string | null>(null);

  const { control, handleSubmit } = useForm<CardBillingFormValues>({
    resolver: zodResolver(cardBillingDetailsSchema),
    defaultValues: defaults,
  });
  const isUS = useController({ name: 'countryIso2', control }).field.value === 'US';

  const label = submitLabel ?? DEFAULT_SUBMIT_LABEL;
  const canSubmit = element.status === 'ready' || verifiedSetupIntentId !== null;

  function restartSetup() {
    setVerifiedSetupIntentId(null);
    setSetupAttempt((n) => n + 1);
  }

  async function submit(values: CardBillingFormValues) {
    setFormError(null);
    setIsSubmitting(true);
    try {
      let setupIntentId = verifiedSetupIntentId;

      if (!setupIntentId) {
        // Stripe.js returns card/validation problems as `result.error` but THROWS for
        // integration mistakes (e.g. an opted-out billing field not passed back).
        // A throw must never leave the button looking dead: surface it inline.
        let result: Awaited<ReturnType<typeof element.confirm>>;
        try {
          result = await element.confirm(CardEntryUtils.toStripeBillingDetails(values));
        } catch (err) {
          console.error('stripe.confirmSetup threw', err);
          cardError(CardEntryUtils.stripeErrorMessage({ type: 'api_error' }));
          return;
        }
        if (result.error) {
          if (CardEntryUtils.needsNewSetupIntent(result.error)) {
            cardError('This card session expired. A fresh secure form has loaded — please enter the card again.');
            restartSetup();
          } else {
            cardError(CardEntryUtils.stripeErrorMessage(result.error));
          }
          return;
        }
        if (result.setupIntent.status !== 'succeeded') {
          cardError('We could not verify this card. Please try again or use a different card.');
          return;
        }
        setupIntentId = result.setupIntent.id;
        setVerifiedSetupIntentId(setupIntentId);
      }

      let saved: ICardSummary;
      try {
        const res = await confirmMutation.mutateAsync({
          setupIntentId,
          billing: CardEntryUtils.toCardBillingDetails(values),
        });
        saved = res.card;
      } catch (err) {
        cardError(CardEntryUtils.confirmErrorMessage(err, label));
        if (CardEntryUtils.needsNewSetupAfterConfirmError(err)) restartSetup();
        return;
      }

      setVerifiedSetupIntentId(null);
      await onSaved(saved);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit(submit)} noValidate>
      <Stack gap="md">
        {formError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title={formError.title}>
            {formError.message}
          </Alert>
        )}

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <ControlledTextInput control={control} name="fullName" label="Name on Card" autoComplete="cc-name" maxLength={BILLING_CONSTRAINTS.FULL_NAME_MAX} required />
          <ControlledTextInput control={control} name="orgName" label="Organization Name" autoComplete="organization" maxLength={BILLING_CONSTRAINTS.ORG_NAME_MAX} />
        </SimpleGrid>

        <ControlledTextInput control={control} name="address1" label="Billing Address" autoComplete="address-line1" maxLength={BILLING_CONSTRAINTS.ADDRESS_MAX} required />

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <ControlledTextInput control={control} name="city" label="City" autoComplete="address-level2" maxLength={BILLING_CONSTRAINTS.CITY_MAX} required />
          <ControlledTextInput control={control} name="region" label={isUS ? 'State' : 'Region'} autoComplete="address-level1" maxLength={BILLING_CONSTRAINTS.REGION_MAX} required />
          <ControlledTextInput control={control} name="postalCode" label={isUS ? 'Zip Code' : 'Postal Code'} autoComplete="postal-code" maxLength={BILLING_CONSTRAINTS.POSTAL_CODE_MAX} required />
        </SimpleGrid>

        <ControlledSelect control={control} name="countryIso2" label="Country" data={COUNTRY_OPTIONS} required />

        <Stack gap={6}>
          <Group gap={6}>
            <IconLock size={14} />
            <Text size="sm" fw={500}>Card Details</Text>
          </Group>

          {setup.isLoading && <Skeleton height={76} radius="sm" />}

          {setup.isError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title="Secure card session unavailable">
              <Stack gap="xs" align="flex-start">
                <Text size="sm">{CardEntryUtils.setupErrorMessage(setup.error)}</Text>
                <Button size="xs" variant="light" onClick={restartSetup}>
                  Try again
                </Button>
              </Stack>
            </Alert>
          )}

          {element.status === 'loading' && (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">Loading secure card form…</Text>
            </Group>
          )}

          {element.status === 'error' && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title="Secure card form unavailable">
              <Stack gap="xs" align="flex-start">
                <Text size="sm">{element.loadError}</Text>
                <Button size="xs" variant="light" onClick={element.retry}>
                  Try again
                </Button>
              </Stack>
            </Alert>
          )}

          {/* Always in the DOM: Stripe mounts its iframe fields here once ready. */}
          <Box ref={element.containerRef} />

          <Text size="xs" c="dimmed">
            Card details go straight into secure fields. Your full card number is never seen or stored by us.
          </Text>
        </Stack>

        <Group justify="flex-end" mt="md">
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" loading={isSubmitting} disabled={!canSubmit}>
            {label}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
